'use client'

import moment from 'moment'
import { Box, Divider } from "@mui/material"
import { AppBreadcrumbs } from "@ui/components"
import { Formik } from "formik"
import { getHomeUrl, getDeliveryNotesUrl } from "@lib/app/appUrls"
import { useClient, usePermissionsContext, useTranslation } from "@theme/base/contexts"
import { useEffect, useRef, useState } from "react"
import { useRemote } from "@hooks"
import { useRouter, notFound, usePathname } from "next/navigation"
import { useUserProfile } from "@theme/base/contexts"
import { IPaginatedRequest } from '@schemas/api'
import { ParsedUrlQuery } from 'querystring'
import { getSelectFilterFromQuery, getStringFromQuery, parseMetaFromQuery } from '@helpers'
import {
    DeliveryNoteSearchRequestInput,
    TPaginatedResponseOfDeliveryNoteSearchResponse,
    TSeasonGetResponse,
} from '@schemas/graphql'
import { VirtualActionsEnum } from '@constants'
import { getQueryFromValues } from '@helpers/url/parseFromQuery'
import { deliveryNotesSearch } from '@logics'

import ListBottomFilters from '@theme/base/components/TableFilters/ListBottomFilters'
import DeliveryNotesLeftFilters from '@theme/base/components/DeliveryNotes/DeliveryNotesLeftFilters'
import DeliveryNotesListTitle from '@theme/base/components/DeliveryNotes/DeliveryNotesListTitle'
import DeliveryNotesTopFilters from '@theme/base/components/DeliveryNotes/DeliveryNotesTopFilters'
import DeliveryNotesList from '@theme/base/components/DeliveryNotes/DeliveryNotesList'

import Grid from "@mui/material/Unstable_Grid2"

export interface DeliveryNoteSearchRequestInputWithMonth extends IPaginatedRequest {
    search: string | undefined
    invoiced: string | undefined
    month: string | undefined
    clientId: string | undefined
    seasonId: string | undefined
}

const getFiltersFromQuery = (searchParams: ParsedUrlQuery, clientId?: string): DeliveryNoteSearchRequestInputWithMonth => {
    return {
        ...parseMetaFromQuery(searchParams),
        search: getStringFromQuery(searchParams, 'search'),
        invoiced: getStringFromQuery(searchParams, 'invoiced'),
        month: getStringFromQuery(searchParams, 'month'),
        clientId: getStringFromQuery(searchParams, 'clientId') ?? clientId,
        seasonId: getSelectFilterFromQuery(searchParams, 'seasonId') ?? '',
    }
}

const filtersToInput = (filters: DeliveryNoteSearchRequestInputWithMonth, seasons : TSeasonGetResponse[]): DeliveryNoteSearchRequestInput => {
    let result : DeliveryNoteSearchRequestInput = {
        page: filters.page,
        perPage: filters.perPage,
        order: '-deliveryDate',
        deliveryDateFrom: filters.month ? moment().subtract(parseInt(filters.month), 'months').toISOString() as any : undefined,
        search: filters.search,
        invoiced: filters.invoiced !== undefined ? filters.invoiced === 'true' : undefined,
        clientId: filters.clientId !== undefined ? filters.clientId === 'all' ? null : filters.clientId : undefined,
    }
    if (filters.seasonId) {
        const season = seasons.find(s => s.id == filters.seasonId)
        result = {...result, createdAtFrom: season?.startDate ?? null, createdAtTo: season?.endDate ?? null}
    }
    return result;
}


export default function DeliveryNotes({searchParams,seasons}: {
        searchParams: ParsedUrlQuery,
        seasons: TSeasonGetResponse[]
    }) {
    const { deliveryNotesReadPerm: deliveryNotesRead } = usePermissionsContext()
    const { getTranslation } = useTranslation()
    const { client } = useClient()

    if (!deliveryNotesRead) {
        notFound()
    }

    const [deliveryNotesResult, setDeliveryNotesResult] = useState<TPaginatedResponseOfDeliveryNoteSearchResponse | null>(null)
    const router = useRouter()
    const pathname = usePathname()
    const { fetchRemote } = useRemote()

    const filters = getFiltersFromQuery(searchParams, client?.id)
    const handleSubmit = async (values: DeliveryNoteSearchRequestInputWithMonth) => {   
        router.push(`${pathname}?${getQueryFromValues(values)}`)
    }

    useEffect(() => {
        const request = filtersToInput(filters, seasons)

        fetchRemote(async () => await deliveryNotesSearch(request)).then(setDeliveryNotesResult)
    }, [JSON.stringify(searchParams)])

    const breadcrumbLinks = [
        { label: getTranslation("common.breadcrumbs.home"), url: getHomeUrl() },
        { label: getTranslation("common.breadcrumbs.mydeliverynotes"), url: getDeliveryNotesUrl() },
    ]

    return (
        <Box mb={10}>
            <AppBreadcrumbs breadcrumbsLinks={breadcrumbLinks} />

            <Formik initialValues={filters} onSubmit={handleSubmit}>
                <Grid container mt={8}>
                    <DeliveryNotesLeftFilters />

                     <Grid xs={0.1} px={4} sx={{ display: { xs: 'none', lg: 'block' } }}>
                        <Divider orientation="vertical" />
                    </Grid>

                    <Grid xs={12} lg={10.5} pl={{ xs: 0, lg: 4 }}>
                        <DeliveryNotesListTitle />

                        <DeliveryNotesTopFilters seasons={seasons} />

                        {deliveryNotesResult && <DeliveryNotesList deliveryNotes={deliveryNotesResult.items} />}

                        {deliveryNotesResult && <ListBottomFilters totalPages={deliveryNotesResult.meta.totalPages ?? 0} />}
                    </Grid>
                </Grid>
            </Formik>
        </Box>
    )
}
